import React from 'react';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, Box, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import TerrainIcon from '@mui/icons-material/Terrain';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import useLogo from '@/hooks/useLogo';
import ClientSelector from '@/components/ui/client-selector/ClientSelector';
import DrawerWrapper from './DrawerWrapper';
import { useAuth } from '@/lib/firebase/auth';
import { useClient } from '@/stores/ClientContext';


const menuItems = [
  // Same structure for the menuItems as before
  {
    items: [
      {
        label: 'dashboard',
        icon: <DashboardIcon />,
        link: '/',
        requiredPermissions: [],
      },
    ],
  },
  {
    heading: 'data tools',
    items: [
      {
        label: 'analyses',
        icon: <TerrainIcon />,
        link: '/data-tools/analyses',
        requiredPermissions: ['data_analysis.user'],
      },
    ],
  },
  {
    heading: 'paid tools',
    items: [
      {
        label: 'bid & budget optimizer',
        icon: <AssessmentIcon />,
        link: '/paid-tools/bid-budget-optimizer',
        requiredPermissions: ['bid_budget_optimizer.user'],
      },
      // {
      //   label: 'ad composer',
      //   icon: <PrecisionManufacturingIcon />,
      //   link: '/paid-tools/ad-composer',
      //   requiredPermissions: ['ad_composer.user'],
      // },
    ],
  },
];

const LeftDrawer = ({ open, toggleDrawer }) => {
  const logo = useLogo();
  const { selectedClient } = useClient();
  const { userDetails } = useAuth();

  // Check if the user has the required permissions for a given client
  const hasPermission = (permissions) => {
    if (!selectedClient || !userDetails) return false;
    const clientPermissions = userDetails?.clientPermissions[selectedClient.id]?.clientPermissions || [];
    return permissions.every(permission => clientPermissions.includes(permission));
  };

  // Generate visible sections based on the user's permissions and the selected client
  const visibleMenuItems = menuItems
    .map(section => ({
      ...section,
      items: section.items.filter(item => hasPermission(item.requiredPermissions)),
    }))
    .filter(section => section.items.length > 0);

  return (
    <DrawerWrapper
      anchor="left"
      open={open}
      toggleDrawer={toggleDrawer}
      drawerContent={
        <>
          {/* Logo and Close Button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1 }}>
            <Box component="img" sx={{ height: 34, width: 34 }} alt="App Logo" src={logo} />
            <IconButton onClick={toggleDrawer}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Client Selector */}
          <Box sx={{ mb: 2, px: 2, py: 1 }}>
            <ClientSelector fullWidth={true} variant="standard" height="34px" /> {/* Custom selector settings */}
          </Box>

          {/* Render filtered menu items */}
          {visibleMenuItems.map((section, index) => (
            <React.Fragment key={index}>
              {section.heading && (
                <Typography
                  variant="subtitle2"
                  sx={{
                    paddingLeft: '16px',
                    paddingTop: '8px',
                    paddingBottom: '4px',
                    fontFamily: 'Monospace, sans-serif',
                    textTransform: 'lowercase',
                    color: 'text.primary',
                  }}
                >
                  {section.heading}
                </Typography>
              )}

              <List>
                {section.items.map(menuItem => (
                  <ListItem key={menuItem.label} disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      component={RouterLink}
                      to={{
                        pathname: menuItem.link,
                        search: `?clientId=${selectedClient?.id || ''}`, // Append clientId to the URL
                      }}
                      onClick={toggleDrawer} // Close the drawer on click
                      sx={{
                        height: 32,
                        padding: '8px',
                        borderRadius: '4px',
                        justifyContent: open ? 'initial' : 'center',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : 'auto',
                          justifyContent: 'center',
                          color: 'text.primary',
                        }}
                      >
                        {menuItem.icon}
                      </ListItemIcon>
                      {open && (
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.primary',
                                fontWeight: 'normal',
                                fontFamily: 'typography.fontFamily',
                                textTransform: 'lowercase',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {menuItem.label}
                            </Typography>
                          }
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              {index < visibleMenuItems.length - 1 && <Divider sx={{ my: 1 }} />}
            </React.Fragment>
          ))}
        </>
      }
    />
  );
};

export default LeftDrawer;
